import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  useEffect(() => { api.get("/invoices").then((r) => { setItems(r.data); setLoading(false); }); }, []);

  if (loading) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading invoices…</div>;

  const statusBadge = (s) => {
    const map = { Draft: "bg-slate-100 text-slate-700 border-slate-200", Sent: "bg-sky-50 text-sky-700 border-sky-200", Paid: "bg-blue-50 text-blue-700 border-blue-200" };
    return <Badge className={`rounded-md font-mono text-[10px] uppercase tracking-widest border ${map[s] || ""}`}>{s}</Badge>;
  };

  return (
    <div data-testid="invoices-page">
      <div className="mb-6">
        <div className="eyebrow mb-2">Invoices · Downloads</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">All invoices.</h1>
      </div>
      {items.length === 0 ? (
        <div data-testid="invoices-empty" className="border border-dashed border-slate-200 rounded-md p-12 text-center text-slate-500">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5}/>
          <div className="text-slate-700 font-medium">No invoices yet.</div>
          <div className="text-sm text-slate-500 mt-1">Invoices are generated after you submit an order and our team reviews it.</div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Invoice</th>
                <th className="text-left px-5 py-3">Order</th>
                <th className="text-left px-5 py-3">Issued</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Total</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.invoice_number} className="border-b border-slate-100 last:border-b-0" data-testid={`invoice-${inv.invoice_number}`}>
                  <td className="px-5 py-4 font-mono text-slate-900">{inv.invoice_number}</td>
                  <td className="px-5 py-4 font-mono text-slate-600">{inv.order_id}</td>
                  <td className="px-5 py-4 text-slate-600">{new Date(inv.issued_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4">{statusBadge(inv.status)}</td>
                  <td className="px-5 py-4 text-right font-mono">${Number(inv.total).toFixed(2)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <Button onClick={() => setView(inv)} variant="ghost" size="sm" className="h-8 text-xs" data-testid={`view-invoice-${inv.invoice_number}`}><Eye className="w-3.5 h-3.5 mr-1" strokeWidth={1.75}/>View</Button>
                      <Button onClick={() => toast.info("PDF export coming in Phase 2.")} variant="ghost" size="sm" className="h-8 text-xs"><Download className="w-3.5 h-3.5 mr-1" strokeWidth={1.75}/>PDF</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Invoice {view?.invoice_number}</DialogTitle>
          </DialogHeader>
          {view && (
            <div>
              <div className="grid grid-cols-2 gap-6 border-b border-slate-100 pb-4">
                <div>
                  <div className="eyebrow mb-2">From</div>
                  <div className="text-sm text-slate-900">Peps 4 Pets, LLC</div>
                  <div className="text-xs text-slate-500">Boulder, CO · USA</div>
                  <div className="text-xs text-slate-500">research@peps4pets.com</div>
                </div>
                <div>
                  <div className="eyebrow mb-2">Details</div>
                  <div className="text-xs text-slate-500">Invoice: <span className="font-mono text-slate-900">{view.invoice_number}</span></div>
                  <div className="text-xs text-slate-500">Order: <span className="font-mono text-slate-900">{view.order_id}</span></div>
                  <div className="text-xs text-slate-500">Issued: {new Date(view.issued_at).toLocaleDateString()}</div>
                  <div className="text-xs text-slate-500">Status: {view.status}</div>
                </div>
              </div>
              <table className="w-full text-sm mt-4">
                <thead className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <tr><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Unit</th><th className="text-right py-2">Line</th></tr>
                </thead>
                <tbody>
                  {view.items.map((it) => (
                    <tr key={it.peptide_slug} className="border-b border-slate-100">
                      <td className="py-2 text-slate-900">{it.name}</td>
                      <td className="py-2 text-right">{it.quantity}</td>
                      <td className="py-2 text-right font-mono">${it.unit_price.toFixed(2)}</td>
                      <td className="py-2 text-right font-mono">${(it.quantity * it.unit_price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 ml-auto max-w-xs text-sm space-y-1">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">${view.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Tax</span><span className="font-mono">${view.tax.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Shipping</span><span className="font-mono">${view.shipping.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 border-t border-slate-200 font-display text-lg font-semibold text-slate-900"><span>Total</span><span className="font-mono">${view.total.toFixed(2)}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
